import { Link } from "react-router-dom";
import { Youtube, ExternalLink, Instagram } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "../lib/api";

const MASK_ICON = "https://customer-assets.emergentagent.com/job_classic-demolition/artifacts/8lulpm32_Gemini_Generated_Image_9hnmyf9hnmyf9hnm.png";

// X/Twitter glyph — lucide-react doesn't ship a current X icon
const XIcon = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const connectLinks = [
  {
    href: "https://www.youtube.com/@DemoPod",
    label: "DemoPod YouTube Channel",
    icon: Youtube,
    testId: "footer-youtube-link",
  },
  {
    href: "https://www.instagram.com/demolitionpod",
    label: "DemoPod Instagram",
    icon: Instagram,
    testId: "footer-demopod-instagram-link",
  },
  {
    href: "https://www.instagram.com/searchanddestroyyou",
    label: "Demolition Instagram",
    icon: Instagram,
    testId: "footer-demolition-instagram-link",
  },
  {
    href: "https://x.com/realdemosmash",
    label: "Demolition Smash on X",
    icon: XIcon,
    testId: "footer-demolition-x-link",
  },
];

export default function Footer() {
  const [disclaimer, setDisclaimer] = useState("");

  useEffect(() => {
    const fetchDisclaimer = async () => {
      try {
        const content = await api.getContent("footer_disclaimer");
        setDisclaimer(content.value || "This is an unofficial fan site.");
      } catch {
        setDisclaimer("This is an unofficial fan site and is not affiliated with WWE.");
      }
    };
    fetchDisclaimer();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <footer className="bg-demolition-paper border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* Brand */}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <img 
                src={MASK_ICON} 
                alt="Demolition Mask" 
                className="w-10 h-10 object-contain" 
              />
              <span className="font-metal text-2xl tracking-tight text-white uppercase">
                AX AND SMASH
              </span>
            </div>
            <p className="text-sm text-white/60 font-mono">
              The Official Fan Page for the greatest tag team in wrestling history.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="font-teko text-xl tracking-widest text-demolition-chrome uppercase">
              QUICK LINKS
            </h3>
            <div className="grid grid-cols-2 gap-2">
              <Link to="/" className="text-white/60 hover:text-demolition-red transition-colors font-mono text-sm">Home</Link>
              <Link to="/about" className="text-white/60 hover:text-demolition-red transition-colors font-mono text-sm">About</Link>
              <Link to="/media" className="text-white/60 hover:text-demolition-red transition-colors font-mono text-sm">Media</Link>
              <Link to="/youtube" className="text-white/60 hover:text-demolition-red transition-colors font-mono text-sm">YouTube</Link>
              <Link to="/events" className="text-white/60 hover:text-demolition-red transition-colors font-mono text-sm">Events</Link>
              <Link to="/shop" className="text-white/60 hover:text-demolition-red transition-colors font-mono text-sm">Shop</Link>
            </div>
          </div>

          {/* Connect */}
          <div className="space-y-4">
            <h3 className="font-teko text-xl tracking-widest text-demolition-chrome uppercase">
              CONNECT
            </h3>
            <div className="space-y-3">
              {connectLinks.map((link) => {
                const Icon = link.icon;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-white/60 hover:text-demolition-red transition-colors group"
                    data-testid={link.testId}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-mono text-sm">{link.label}</span>
                    <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </a>
                );
              })}
            </div>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <p className="text-xs text-white/40 font-mono text-center max-w-4xl mx-auto" data-testid="footer-disclaimer">
            {disclaimer}
          </p>
          <p className="text-xs text-white/30 font-mono text-center mt-4">
            &copy; {new Date().getFullYear()} Official Demolition Fan Page. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
